import type { VariationRateScope } from '@/types/api/variation-scope.types';
import { CartItem } from '@/types/api/carts.types';
import { Document } from '@/types/api/items.types';
import { Vendor } from './vendors.types';
import { PRReturn } from '@/types/api/organisations.types';
import { Status } from '@/common/constants';

export type CreateOrdersBody = {
  cartId: string;
  paymentInformation: PaymentInformation[];
};

export type Deposit = {
  vendorId: string;
  vendorName: string;
  deposit: number;
};

export enum PaymentOptionsEnum {
  COD = 'cash',
  ACCOUNT = 'account',
  DEPOSIT = 'deposit',
  NONE = 'none',
}

export enum BackendPaymentMethodEnum {
  COD = 'COD',
  ACCOUNT = 'Account',
}

export type CreateOrderOrClaimRequestBody = {
  cartId: string;
  paymentInformation: PaymentInformation;
};

export type PaymentInformation = {
  vendorId: string;
  deposit: CartDeposit;
  order: CartOrder;
};

export type CartDeposit = {
  percent: number | null;
  dueDate: string | null;
  assigneeId: string | null;
};

export type CartOrder = {
  dueDate?: string;
  method: string;
  assigneeId?: string;
};

export type Addition = {
  id: string;
  type: 'deposit' | 'retention';
  vendorId: string;
  vendorName: string;
  percent: number;
  paymentType: string;
  dueDate: string;
  assigneeId: string;
};

export interface StagedOrderDocument {
  file: File;
  documentType: 'Quote' | 'Invoice';
}

export interface LocalDepositTracking {
  vendorId: string;
  vendorName: string;
  amountToPayVendor: number;
  amountExclVat: number;
  requestType?: 'Claim' | 'Order';
  hasOtherType?: boolean;
  totalDeductions?: number;
  additions?: Addition[];
  orderPaymentMethod?: string;
  orderDueDate?: string;
  orderAssigneeId?: string;
  stagedDocuments?: StagedOrderDocument[];
}

export type Order = {
  id: string;
  orderNumber: string;
  boqId: string;
  siteId: string;
  siteName: string;
  organisationId: string;
  vendorName: string;
  status: string;
  notes: string;
  deliveries: Delivery[];
  deliveryDate: string;
  ordered: boolean;
  amount: number;
  amountPaid: number;
  vat: number;
  items: OrderItem[];
  createdAt: string;
  createdBy: string;
  createdByName: string;
  reviewComments: string;
  projectId: string;
  vendorId: string;
  deposit: number;
  documents: Document[];
  vendorDetails: Vendor;
  returns: OrderReturn[];
  version: number;
};

export type Delivery = {
  date: string;
  id: string;
  items: {
    orderItemId: string;
    orderItemName: string;
    quantity: number;
    rate: number;
    returnedQuantity: number;
    valueDelivered: number;
  }[];
};

export type DocumentUploadResponse = {
  id: string;
  url: string;
  name: string;
  expiry?: string;
  urlExpiry?: string;
  path?: string;
};

export type ValueLog = {
  createdAt: string;
  createdById: string;
  deliveryId: string;
  quantity: number;
  rate: number;
  returnId: string;
  total: number;
};

export type OrderItem = CartItem & {
  valueLogs?: ValueLog[];
};

export type OrderStatusUpdateBody = {
  status: string;
  reviewComments: string;
  notifyVendor?: boolean;
  version: number;
  /** Sent only on an approval the site-dates dialog asked about: it lands in the site's log. */
  siteDateChangeReason?: string;
};

/**
 * The item variation review body (`ItemVariationReviewDto`, PE-967).
 *
 * Context: the backend ignores `version` on this route, so the hub, which holds no child order
 * version, omits it rather than inventing one.
 */
export type ItemVariationReviewBody = Omit<
  OrderStatusUpdateBody,
  'version' | 'siteDateChangeReason'
> & {
  version?: number;
  /** Sent only on approving a variation whose `bulkDetails.rateScopeApplies` is true. */
  rateScope?: VariationRateScope;
};

/**
 * The bulk order an order is a child of, denormalised onto the child at creation (PE-625).
 *
 * Context: **absent on a standard order, and that absence is how a bulk child is told from an
 * ordinary one** — there is no boolean flag. It arrives as one nested object rather than the two
 * flat fields the tickets describe, and it carries the parent's owner as well as its reference, so
 * FR-49's "reference **+ creator identity**" is satisfied with no extra call.
 */
export type OrderParentBulkOrder = {
  /** The parent's id — the hard link. */
  id: string;
  /** The parent's `BLK…` reference. Also what the orders search matches on. */
  reference: string;
  /**
   * The person who created the bulk order.
   *
   * Context: a site-scoped user reads a bulk child but cannot open the parent hub, so this is the
   * only place they can find out who to contact about it (FR-49, DL-58).
   */
  ownerId: string;
  ownerName: string;
  /**
   * The bulk order's project, so a link to it is asked of that project (PE-1221). The empty GUID
   * on a site order placed before it was served: read it through `parentBulkOrderProjectId`.
   */
  projectId?: string;
};

export type OrderListItem = {
  id: string;
  orderNumber: string;
  siteId: string;
  siteName: string;
  organisationId: string;
  vendorId: string;
  vendorName: string;
  status: string;
  notes: string;
  deliveryDate: string;
  ordered: boolean;
  amount: number;
  amountPaid: number;
  vat: number;
  amountWithVariations: number;
  vatWithVariations: number;
  createdAt: string;
  createdBy: string;
  deposit: number;
  version: number;
  /** Present only on a bulk child. Absent on a standard order — see {@link OrderParentBulkOrder}. */
  parentBulkOrder?: OrderParentBulkOrder;
};

export type GetOrdersParams = {
  pageNo?: number;
  pageSize?: number;
  orderBy?: string;
  orderByDesc?: string;
  siteId?: string;
  status?: string;
  vendorId?: string;
  orderNumber?: string;
  type?: string;
  startDate?: string;
  endDate?: string;
};

export type OrderPaymentRequest = {
  id: string;
  paymentRequestNumber: string;
  accountsPayableId: string;
  date: string;
  siteId: string;
  siteName: string;
  projectId: string;
  organisationId: string;
  boqId: string;
  vendorId: string;
  vendorName: string;
  order: {
    id: string;
    orderNumber: string;
    deliveryDate: string;
    notes: string;
    amount: number;
  };
  status: string;
  amount: number;
  amountInclVat: number;
  type: string;
  reviews: ReviewItem[];
  changelog: ChangeLogItem[];
  returns: PRReturn[];
};

export type ChangeLogItem = {
  amount: number;
  amountInclVat: number;
  createdAt: string;
  createdBy: string;
  description: string;
};

export type ReviewItem = {
  reviewedBy: string;
  roleCode: string;
  result: string;
  declineReason: string;
};

export type OrderDelivery = {
  itemId: string;
  quantity: number;
  rate: number;
};

export type OrderTotals = {
  pendingTotal: number;
  pendingTotalInclVat: number;
  approvedTotal: number;
  approvedTotalInclVat: number;
  pendingOrderCount: number;
};

export type ShareOrder = {
  type: 'VendorEmail' | 'SelfEmail';
  version: number;
};

export type Retention = {
  id: string;
  percent: number;
  amount: number;
  previous: number;
  createdAt: string;
  createdBy: string;
  vendorId?: string;
  assigneeId?: string;
  assigneeName?: string;
  dueDate?: string;
};

export type ValueContraCharge = {
  id: string;
  amount: number;
  createdAt: string;
  createdBy: string;
};

export type ItemContraCharge = {
  id: string;
  name: string;
  /**
   * Context: the order payload does not carry this today, so the drawer's charge rows show no unit
   * beside their quantity — the pre-rework table's Unit column was blank for the same reason. Typed
   * optional rather than dropped so the cell is ready when the API starts sending it.
   */
  unitType?: string;
  spec: string;
  boqItemPath: number[];
  boqItemInGroup: boolean;
  sectionName: string;
  quantity: number;
  rate: number;
  total: number;
  createdAt: string;
  createdBy: string;
};

export type Deduction = {
  total: number;
  retentions: Retention[];
  valueContraCharges: ValueContraCharge[];
  itemContraCharges: ItemContraCharge[];
};

export type OrderReturnItem = {
  orderDeliveryId: string;
  orderItemId: string;
  quantity: number;
  rate: number;
  returnItemId: string;
  total: number;
};

export type OrderReturn = {
  createdAt: string;
  returnId: string;
  returnNumber: string;
  total: number;
  totalInclVat: number;
  type: string;
  reversal: boolean;
  items: OrderReturnItem[];
};

export type OrderPaymentDetails = {
  accType: string | null;
  assigneeId: string | null;
  assigneeName: string | null;
  dueDate: string | null;
  method: string;
};

export type OrderClaim = {
  id: string;
  type: string;
  orderNumber: string;
  boqId: string;
  siteId: string;
  siteName: string;
  organisationId: string;
  vendorName: string;
  vendorDetails: Vendor;
  status: Status;
  notes: string;
  deliveryDate: string;
  ordered: boolean;
  amount: number;
  amountPaid: number;
  vat: number;
  amountWithVariations: number;
  vatWithVariations: number;
  items: OrderItem[];
  deductions: Deduction;
  createdAt: string;
  createdBy: string;
  createdByName: string;
  completedAt?: string;
  completedBy?: string;
  completedByName?: string;
  reviewComments: string;
  completionComments?: string;
  projectId: string;
  vendorId: string;
  deposit: number;
  depositPercent: number;
  documents: Document[];
  deliveries: Delivery[];
  cart: boolean;
  approvedAt: string;
  reviewedBy: string;
  reviewedByName: string;
  periodTotal: number;
  period: number;
  returns: OrderReturn[];
  paymentDetails: OrderPaymentDetails;
  version: number;
  /** Present only on a bulk child. Absent on a standard order (PE-625). */
  parentBulkOrder?: OrderParentBulkOrder;
};

export type RetentionData = {
  id: string;
  itemName: string;
  unit: string;
  previous: number;
  qty: number;
  total: number;
  vendorId: string;
};

export type ValueContraChargeData = {
  id: string;
  itemName: string;
  amount: number;
  vendorId: string;
};

export type ItemContraChargeRequest = {
  itemPath: number[];
  itemInGroup: boolean;
  rate: number;
  quantity: number;
};

export type OrderTotalParams = {
  orgId: string;
  siteId?: string;
  status?: string;
  vendorId?: string;
  orderNumber?: string;
  type?: string;
};

export type OrderTotalType =
  | 'orders'
  | 'claims'
  | 'site-orders'
  | 'site-claims'
  | 'vendor-orders'
  | 'vendor-claims';

export type OrderStatus =
  | 'Requested'
  | 'Approved'
  | 'Rejected'
  | 'PartiallyDelivered'
  | 'Delivered'
  | 'Cancelled'
  | 'Complete'
  | 'Returned';

export type ComplianceRule = {
  name: string;
  displayName: string;
  description: string;
  isBlocking: boolean;
  ruleType: string;
  isCompliant: boolean;
  failureReason: string;
  metadata: {
    documentType?: string;
    conditionalOn?: string;
  };
};

export type ComplianceInfo = {
  rules: ComplianceRule[];
  overallCompliance: {
    isCompliant: boolean;
    failures: string[];
    warnings: string[];
  };
};

export interface DocumentBlob extends Blob {
  documentType?: string;
}
